import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { mockCases } from '@/mocks/cases';
import { mockChildren } from '@/mocks/children';

const reportTypes = ['Case Summary', 'Statistical Analysis', 'Compliance Report', 'Audit Trail', 'Custom Report'];

export default function ReportsPage() {
  const [activeReport, setActiveReport] = useState('Case Summary');
  const [dateFrom, setDateFrom] = useState('2026-01-01');
  const [dateTo, setDateTo] = useState('2026-04-09');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');
  const [showGenerated, setShowGenerated] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveCase, setArchiveCase] = useState('');

  const totalCases = mockCases.length;
  const activeCases = mockCases.filter((c) => c.status === 'active').length;
  const closedCases = mockCases.filter((c) => c.status === 'closed').length;
  const criticalCases = mockCases.filter((c) => c.riskLevel === 'critical').length;
  const highCases = mockCases.filter((c) => c.riskLevel === 'high').length;
  const referredCases = mockCases.filter((c) => c.status === 'referred').length;

  const filteredCases = mockCases.filter((c) => {
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchRisk = filterRisk === 'all' || c.riskLevel === filterRisk;
    return matchStatus && matchRisk;
  });

  const auditLog = [
    { user: 'Maria Santos', action: 'Viewed case DCPCMS-2026-00001', timestamp: '2026-04-09 09:15', ip: '192.168.1.10', role: 'Social Worker' },
    { user: 'Maria Santos', action: 'Created case DCPCMS-2026-00006', timestamp: '2026-04-09 08:30', ip: '192.168.1.10', role: 'Social Worker' },
    { user: 'Jose Reyes', action: 'Approved case DCPCMS-2026-00001', timestamp: '2026-04-07 09:30', ip: '192.168.1.20', role: 'Supervisor' },
    { user: 'Maria Santos', action: 'Uploaded document to DCPCMS-2026-00003', timestamp: '2026-04-07 11:00', ip: '192.168.1.10', role: 'Social Worker' },
    { user: 'Ana Cruz', action: 'Generated monthly report', timestamp: '2026-04-06 14:00', ip: '192.168.1.30', role: 'Admin' },
    { user: 'Maria Santos', action: 'Updated risk assessment for DCPCMS-2026-00002', timestamp: '2026-04-07 16:45', ip: '192.168.1.10', role: 'Social Worker' },
    { user: 'Jose Reyes', action: 'Reviewed case DCPCMS-2026-00002', timestamp: '2026-04-08 10:00', ip: '192.168.1.20', role: 'Supervisor' },
    { user: 'Ana Cruz', action: 'Exported case data', timestamp: '2026-04-05 09:00', ip: '192.168.1.30', role: 'Admin' },
  ];

  const archivedCases = mockCases.filter((c) => c.status === 'closed' || c.status === 'archived');

  return (
    <AppLayout title="Reports & Archiving" breadcrumb={['Home', 'Reports & Archiving']}>
      {/* Report Type Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {reportTypes.map((rt) => (
          <button
            key={rt}
            onClick={() => setActiveReport(rt)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap
              ${activeReport === rt ? 'bg-teal-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
          >
            {rt}
          </button>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 whitespace-nowrap">From:</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-500" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 whitespace-nowrap">To:</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-teal-500" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
          <option value="referred">Referred</option>
        </select>
        <select value={filterRisk} onChange={(e) => setFilterRisk(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none cursor-pointer">
          <option value="all">All Risk Levels</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="moderate">Moderate</option>
          <option value="low">Low</option>
        </select>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setShowGenerated(true)} className="bg-teal-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-file-chart-line" />Generate Report
          </button>
          <button className="border border-gray-200 text-gray-700 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-download-line" />Export CSV
          </button>
          <button className="border border-gray-200 text-gray-700 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-file-pdf-line" />Export PDF
          </button>
        </div>
      </div>

      {/* Case Summary */}
      {activeReport === 'Case Summary' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Total Cases', value: totalCases, color: 'text-gray-800', bg: 'bg-gray-50' },
              { label: 'Active', value: activeCases, color: 'text-teal-600', bg: 'bg-teal-50' },
              { label: 'Closed', value: closedCases, color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Critical', value: criticalCases, color: 'text-red-600', bg: 'bg-red-50' },
              { label: 'High Risk', value: highCases, color: 'text-orange-600', bg: 'bg-orange-50' },
              { label: 'Referred', value: referredCases, color: 'text-blue-600', bg: 'bg-blue-50' },
            ].map((s) => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4 text-center border border-gray-100`}>
                <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-gray-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Incident Type Breakdown */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h4 className="font-semibold text-gray-800 mb-4">Cases by Incident Type</h4>
            {['Physical Abuse', 'Sexual Abuse', 'Neglect', 'Child Labor', 'Emotional Abuse'].map((type) => {
              const count = mockCases.filter((c) => c.incidentType === type).length;
              const pct = Math.round((count / totalCases) * 100);
              return (
                <div key={type} className="mb-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{type}</span>
                    <span className="font-semibold text-gray-800">{count} ({pct}%)</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Case Table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h4 className="font-semibold text-gray-800">Case Records ({filteredCases.length})</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <th className="px-5 py-3 text-left font-medium">Case No.</th>
                    <th className="px-5 py-3 text-left font-medium">Child</th>
                    <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Incident</th>
                    <th className="px-5 py-3 text-left font-medium">Risk</th>
                    <th className="px-5 py-3 text-left font-medium">Status</th>
                    <th className="px-5 py-3 text-left font-medium hidden lg:table-cell">Worker</th>
                    <th className="px-5 py-3 text-left font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredCases.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 text-xs font-mono text-gray-500">{c.caseNumber}</td>
                      <td className="px-5 py-3">
                        <p className="text-sm font-medium text-gray-800">{c.childName}</p>
                        <p className="text-xs text-gray-400">{c.age} yrs · {c.gender}</p>
                      </td>
                      <td className="px-5 py-3 hidden md:table-cell text-xs text-gray-600">{c.incidentType}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${c.riskLevel === 'critical' ? 'bg-red-100 text-red-700' : c.riskLevel === 'high' ? 'bg-orange-100 text-orange-700' : c.riskLevel === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                          {c.riskLevel}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${c.status === 'active' ? 'bg-teal-100 text-teal-700' : c.status === 'closed' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>
                          {c.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-5 py-3 hidden lg:table-cell text-xs text-gray-600">{c.assignedWorker}</td>
                      <td className="px-5 py-3">
                        {(c.status === 'closed' || c.status === 'archived') ? (
                          <button onClick={() => { setArchiveCase(c.caseNumber); setShowArchiveModal(true); }} className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap">Archive</button>
                        ) : (
                          <button className="text-xs text-teal-500 hover:text-teal-700 cursor-pointer whitespace-nowrap">View</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Statistical Analysis */}
      {activeReport === 'Statistical Analysis' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h4 className="font-semibold text-gray-800 mb-4">Cases by Municipality</h4>
              {['Batangas City', 'Lipa City', 'Tanauan City'].map((city) => {
                const count = mockCases.filter((c) => c.municipality === city).length;
                const pct = Math.round((count / totalCases) * 100);
                return (
                  <div key={city} className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">{city}</span>
                      <span className="font-semibold">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h4 className="font-semibold text-gray-800 mb-4">Age Group Distribution</h4>
              {[['0-5 years', mockChildren.filter((c) => c.age <= 5).length], ['6-10 years', mockChildren.filter((c) => c.age >= 6 && c.age <= 10).length], ['11-15 years', mockChildren.filter((c) => c.age >= 11 && c.age <= 15).length], ['16-17 years', mockChildren.filter((c) => c.age >= 16).length]].map(([label, count]) => {
                const pct = Math.round((Number(count) / mockChildren.length) * 100);
                return (
                  <div key={String(label)} className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">{label}</span>
                      <span className="font-semibold">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h4 className="font-semibold text-gray-800 mb-4">Gender Distribution</h4>
              {['Male', 'Female'].map((g) => {
                const count = mockChildren.filter((c) => c.gender === g).length;
                const pct = Math.round((count / mockChildren.length) * 100);
                return (
                  <div key={g} className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">{g}</span>
                      <span className="font-semibold">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${g === 'Male' ? 'bg-teal-500' : 'bg-pink-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h4 className="font-semibold text-gray-800 mb-4">Risk Level Distribution</h4>
              {['critical', 'high', 'moderate', 'low'].map((level) => {
                const count = mockCases.filter((c) => c.riskLevel === level).length;
                const pct = Math.round((count / totalCases) * 100);
                const color = level === 'critical' ? 'bg-red-500' : level === 'high' ? 'bg-orange-500' : level === 'moderate' ? 'bg-amber-500' : 'bg-green-500';
                return (
                  <div key={level} className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700 capitalize">{level}</span>
                      <span className="font-semibold">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail */}
      {activeReport === 'Audit Trail' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h4 className="font-semibold text-gray-800">User Activity Audit Trail</h4>
            <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200">
              <i className="ri-shield-check-line" />RA 10173 Compliant
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-5 py-3 text-left font-medium">Timestamp</th>
                  <th className="px-5 py-3 text-left font-medium">User</th>
                  <th className="px-5 py-3 text-left font-medium">Role</th>
                  <th className="px-5 py-3 text-left font-medium">Action</th>
                  <th className="px-5 py-3 text-left font-medium hidden md:table-cell">IP Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {auditLog.map((log, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 text-xs font-mono text-gray-500 whitespace-nowrap">{log.timestamp}</td>
                    <td className="px-5 py-3 text-sm font-medium text-gray-800">{log.user}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${log.role === 'Supervisor' ? 'bg-amber-100 text-amber-700' : log.role === 'Admin' ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'}`}>
                        {log.role}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600">{log.action}</td>
                    <td className="px-5 py-3 text-xs font-mono text-gray-400 hidden md:table-cell">{log.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Compliance Report */}
      {activeReport === 'Compliance Report' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <i className="ri-shield-check-line text-teal-600 text-xl" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Data Privacy Compliance Report</h4>
                <p className="text-sm text-gray-500">Republic Act No. 10173 — Data Privacy Act of 2012</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'Data Encryption', status: 'Compliant', icon: 'ri-lock-line', desc: 'All case data encrypted at rest and in transit' },
                { label: 'Access Control', status: 'Compliant', icon: 'ri-user-settings-line', desc: 'Role-based access control implemented' },
                { label: 'Audit Logging', status: 'Compliant', icon: 'ri-file-list-3-line', desc: 'All user actions logged with timestamps' },
                { label: 'Data Minimization', status: 'Compliant', icon: 'ri-database-2-line', desc: 'Only necessary data collected per case' },
                { label: 'Consent Management', status: 'Compliant', icon: 'ri-checkbox-circle-line', desc: 'Guardian consent documented for all cases' },
                { label: 'Breach Notification', status: 'Configured', icon: 'ri-alarm-line', desc: 'Automated alerts for unauthorized access' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-green-600`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-800">{item.label}</p>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{item.status}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Custom Report */}
      {activeReport === 'Custom Report' && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h4 className="font-semibold text-gray-800 mb-4">Customizable LGU Report Builder</h4>
          <p className="text-sm text-gray-500 mb-6">Select the fields and filters to generate a custom report for your LGU requirements.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Report Title</label>
              <input placeholder="e.g. Batangas City Q1 2026 Child Protection Report" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">LGU / Agency</label>
              <input placeholder="e.g. Batangas City SWDO" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
            </div>
          </div>
          <div className="mb-6">
            <label className="text-sm font-medium text-gray-700 mb-3 block">Include Sections</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {['Case Summary', 'Child Demographics', 'Incident Types', 'Risk Levels', 'Referrals', 'Interventions', 'Outcomes', 'Audit Trail', 'Compliance Status'].map((section) => (
                <label key={section} className="flex items-center gap-2 cursor-pointer bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100 hover:border-teal-200 transition-colors">
                  <input type="checkbox" defaultChecked className="rounded border-gray-300 text-teal-500" />
                  <span className="text-sm text-gray-700">{section}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowGenerated(true)} className="bg-teal-500 text-white text-sm px-6 py-2.5 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-file-chart-line" />Generate Custom Report
            </button>
            <button className="border border-gray-200 text-gray-700 text-sm px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-save-line" />Save Template
            </button>
          </div>
        </div>
      )}

      {/* Archived Cases */}
      <div className="mt-6 bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <i className="ri-archive-line text-gray-500" />
          <h4 className="font-semibold text-gray-800">Case Archives</h4>
          <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{archivedCases.length} cases</span>
        </div>
        <div className="divide-y divide-gray-50">
          {archivedCases.map((c) => (
            <div key={c.id} className="px-5 py-4 flex items-center gap-4">
              <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-folder-line text-gray-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">{c.childName} — <span className="font-mono text-xs text-gray-500">{c.caseNumber}</span></p>
                <p className="text-xs text-gray-400">{c.incidentType} · Closed {c.updatedAt} · {c.assignedWorker}</p>
              </div>
              <div className="flex gap-2">
                <button className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-1">
                  <i className="ri-eye-line" />View
                </button>
                <button className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-1">
                  <i className="ri-download-line" />Export
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Generated Report Modal */}
      {showGenerated && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-chart-line text-teal-500 text-3xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Report Generated!</h3>
            <p className="text-sm text-gray-500 mb-4">Your report has been generated successfully and is ready for download.</p>
            <div className="bg-gray-50 rounded-xl p-3 mb-4 text-left border border-gray-100">
              <p className="text-xs text-gray-500">Report: <strong>{activeReport}</strong></p>
              <p className="text-xs text-gray-500">Period: <strong>{dateFrom} to {dateTo}</strong></p>
              <p className="text-xs text-gray-500">Generated: <strong>2026-04-09 {new Date().toLocaleTimeString()}</strong></p>
            </div>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setShowGenerated(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap">Close</button>
              <button onClick={() => setShowGenerated(false)} className="text-sm bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                <i className="ri-download-line" />Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirm Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Archive</h3>
            <p className="text-sm text-gray-500 mb-4">Archive case <strong>{archiveCase}</strong>? This will move it to long-term storage.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowArchiveModal(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => setShowArchiveModal(false)} className="text-sm bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors cursor-pointer whitespace-nowrap">Archive</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
